import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { mongo } from '../server/mongo.js';

export type WhatsappDeliveryMode = 'fallback' | 'both';

export interface InstitutionWhatsappConfigData {
  institutionID: string;
  enabled: boolean;
  deliveryMode: WhatsappDeliveryMode;
  appId: string;
  appSecret: string;
  businessId: string;
  phoneNumberId: string;
  accessToken: string;
  apiVersion: Maybe<string>;
}

type InstitutionWhatsappConfigDocument = InstitutionWhatsappConfigData & { _id: string };

const maskSecret = (value: Maybe<string>) => `***${value?.slice(-4) ?? ''}`;
const isMaskedSecret = (value: Maybe<string>) => value?.startsWith('***') ?? false;

@Entity()
export class InstitutionWhatsappConfig extends MongoEntity {
  static override _class = 'InstitutionWhatsappConfig';
  static override _collectionName = 'institution-whatsapp-configs';

  @Field(type => String)
  institutionID!: string;

  @Field(type => Boolean, { default: value => value ?? false })
  enabled!: boolean;

  @Field(type => String, { default: value => (value === 'both' ? 'both' : 'fallback') })
  deliveryMode!: WhatsappDeliveryMode;

  @Field(type => String)
  appId!: string;

  // BaseQL has no field-level write-only permission. These fields therefore resolve only to a mask;
  // server code that needs the credentials must use getInstitutionWhatsappConfigRaw below.
  @Field(type => String, {
    includeInAllFieldSelectionSet: false,
    includeInParameter: true,
    resolve: function (this: InstitutionWhatsappConfig) {
      return async () => maskSecret(this.appSecret);
    },
  })
  appSecret!: string;

  @Field(type => String)
  businessId!: string;

  @Field(type => String)
  phoneNumberId!: string;

  @Field(type => String, {
    includeInAllFieldSelectionSet: false,
    includeInParameter: true,
    resolve: function (this: InstitutionWhatsappConfig) {
      return async () => maskSecret(this.accessToken);
    },
  })
  accessToken!: string;

  @Field(type => String)
  apiVersion: Maybe<string>;

  static override async transformBeforeUpdate(
    oldEntity: InstitutionWhatsappConfig,
    update: InstitutionWhatsappConfig,
  ): Promise<void> {
    await super.transformBeforeUpdate(oldEntity, update);

    // Clearing credentials is done by deleting the config, not by blanking fields.
    if (isMaskedSecret(update.appSecret) || !update.appSecret?.trim()) update.appSecret = oldEntity.appSecret;
    if (isMaskedSecret(update.accessToken) || !update.accessToken?.trim()) update.accessToken = oldEntity.accessToken;
  }

  static override async beforeCreate(entity: InstitutionWhatsappConfig): Promise<void> {
    await super.beforeCreate(entity);
    const collection = mongo().collection<InstitutionWhatsappConfigDocument>(InstitutionWhatsappConfig._collectionName);
    const existing = await collection.findOne({ institutionID: entity.institutionID });
    if (existing) throw new Error('Only one WhatsApp configuration is allowed per institution.');
  }

  static override async beforeUpdate(
    oldEntity: InstitutionWhatsappConfig,
    update: InstitutionWhatsappConfig,
  ): Promise<void> {
    await super.beforeUpdate(oldEntity, update);
    const institutionID = update.institutionID ?? oldEntity.institutionID;
    const collection = mongo().collection<InstitutionWhatsappConfigDocument>(InstitutionWhatsappConfig._collectionName);
    const existing = await collection.findOne({ institutionID, _id: { $ne: oldEntity._id } });
    if (existing) throw new Error('Only one WhatsApp configuration is allowed per institution.');
  }
}

// The unique index is the final guard against concurrent creates for the same institution.
export async function ensureInstitutionWhatsappConfigIndexes(): Promise<void> {
  try {
    const collection = mongo().collection(InstitutionWhatsappConfig._collectionName);
    await collection.createIndex({ institutionID: 1 }, { unique: true });
  } catch (err) {
    logger.error({ err }, 'failed to create unique institution whatsapp configuration index');
  }
}

/** Server-only credential loader. It deliberately bypasses GraphQL field resolvers. */
export async function getInstitutionWhatsappConfigRaw(
  institutionID: string,
): Promise<InstitutionWhatsappConfigData | null> {
  const collection = mongo().collection<InstitutionWhatsappConfigDocument>(InstitutionWhatsappConfig._collectionName);
  const config = await collection.findOne({ institutionID });
  if (!config) return null;

  return {
    institutionID: config.institutionID,
    enabled: config.enabled === true,
    deliveryMode: config.deliveryMode === 'both' ? 'both' : 'fallback',
    appId: config.appId,
    appSecret: config.appSecret,
    businessId: config.businessId,
    phoneNumberId: config.phoneNumberId,
    accessToken: config.accessToken,
    apiVersion: config.apiVersion,
  };
}
